import { useState } from "react";
import { CheckCircle2, Paperclip, Loader2 } from "lucide-react";
import { postEnquiry } from "../lib/api";
import { PROJECT_TYPES, SERVICE_OPTIONS, PROJECT_STAGES, BUDGET_RANGES } from "../data/content";
import CTAButton from "./CTAButton";

const inputCls =
  "w-full bg-white/60 border border-white/70 focus:border-steel px-4 py-3.5 rounded-2xl text-[15px] text-coal placeholder:text-graphite/50 outline-none transition-colors duration-300 min-h-[44px]";
const labelCls = "label-tech text-graphite block mb-2.5";
const errCls = "text-xs text-red-700 mt-1.5";

const Field = ({ label, error, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className={labelCls}>{label}</label>
    {children}
    {error && <p role="alert" className={errCls} data-testid={`error-${htmlFor}`}>{error}</p>}
  </div>
);

const initial = {
  name: "", company: "", email: "", phone: "", project_location: "",
  project_type: "", services: [], project_stage: "", budget_range: "",
  completion_date: "", message: "", consent: false, attachment: null,
};

const ContactForm = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (s) =>
    set("services", form.services.includes(s) ? form.services.filter((x) => x !== s) : [...form.services, s]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.message.trim()) e.message = "Please tell us briefly about your project.";
    if (!form.consent) e.consent = "Please confirm your consent to be contacted.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === "services") v.forEach((s) => fd.append("services", s));
        else if (k === "attachment") { if (v) fd.append("attachment", v); }
        else fd.append(k, String(v));
      });
      await postEnquiry(fd);
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="form-success" className="glass rounded-3xl p-10 sm:p-14 text-center">
        <CheckCircle2 size={40} className="mx-auto text-cobalt mb-6" strokeWidth={1.5} />
        <h3 className="t-h3 text-coal mb-4">Thank you.</h3>
        <p className="text-graphite max-w-md mx-auto leading-relaxed">
          Your enquiry has been received. Our team will respond shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="contact-form" className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-7">
      <Field label="Name *" error={errors.name} htmlFor="name">
        <input id="name" data-testid="field-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" />
      </Field>
      <Field label="Company" htmlFor="company">
        <input id="company" data-testid="field-company" className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} autoComplete="organization" />
      </Field>
      <Field label="Email *" error={errors.email} htmlFor="email">
        <input id="email" type="email" data-testid="field-email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
      </Field>
      <Field label="Project location" htmlFor="project_location">
        <input id="project_location" data-testid="field-location" className={inputCls} value={form.project_location} onChange={(e) => set("project_location", e.target.value)} placeholder="City, Country" />
      </Field>
      <Field label="Project type" htmlFor="project_type">
        <select id="project_type" data-testid="field-project-type" className={inputCls} value={form.project_type} onChange={(e) => set("project_type", e.target.value)}>
          <option value="">Select…</option>
          {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <span className={labelCls} id="services-label">Required services</span>
        <div role="group" aria-labelledby="services-label" className="flex flex-wrap gap-2.5" data-testid="field-services">
          {SERVICE_OPTIONS.map((s) => (
            <button
              type="button"
              key={s}
              data-testid={`service-option-${s.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => toggleService(s)}
              aria-pressed={form.services.includes(s)}
              className={`label-tech px-4 py-2.5 border rounded-full transition-colors duration-300 min-h-[44px] ${
                form.services.includes(s) ? "bg-coal text-paper border-coal" : "border-ink/15 text-graphite hover:border-coal hover:text-coal"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      <Field label="Estimated project stage" htmlFor="project_stage">
        <select id="project_stage" data-testid="field-stage" className={inputCls} value={form.project_stage} onChange={(e) => set("project_stage", e.target.value)}>
          <option value="">Select…</option>
          {PROJECT_STAGES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Estimated budget range" htmlFor="budget_range">
        <select id="budget_range" data-testid="field-budget" className={inputCls} value={form.budget_range} onChange={(e) => set("budget_range", e.target.value)}>
          <option value="">Select…</option>
          {BUDGET_RANGES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Desired completion date" htmlFor="completion_date">
        <input id="completion_date" data-testid="field-completion" className={inputCls} value={form.completion_date} onChange={(e) => set("completion_date", e.target.value)} placeholder="e.g. Q4 2027" />
      </Field>
      <Field label="Brief, plans or reference material" htmlFor="attachment">
        <label className={`${inputCls} flex items-center gap-3 cursor-pointer hover:border-cobalt`}>
          <Paperclip size={16} className="text-graphite shrink-0" />
          <span className="truncate text-graphite/70">{form.attachment ? form.attachment.name : "Attach file (max 15 MB)"}</span>
          <input id="attachment" type="file" data-testid="field-attachment" className="sr-only" onChange={(e) => set("attachment", e.target.files?.[0] || null)} />
        </label>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Message *" error={errors.message} htmlFor="message">
          <textarea id="message" rows={5} data-testid="field-message" className={`${inputCls} resize-y`} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="What are you building, where is it located, and which services do you require?" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="field-consent">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={(e) => set("consent", e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#434A54]"
            aria-label="Consent to be contacted"
          />
          <span className="text-sm text-graphite leading-relaxed">
            I consent to Arch Expo AG processing my details to respond to this enquiry. *
          </span>
        </label>
        {errors.consent && <p role="alert" className={errCls} data-testid="error-consent">{errors.consent}</p>}
      </div>
      {status === "error" && (
        <p role="alert" data-testid="form-error" className="sm:col-span-2 text-sm text-red-700">
          Something went wrong while sending your enquiry. Please try again or email us directly.
        </p>
      )}
      <div className="sm:col-span-2 pt-2">
        <CTAButton type="submit" variant="blue" testId="form-submit" className="w-full sm:w-auto">
          {status === "sending" ? (
            <span className="inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Sending…</span>
          ) : (
            "Submit Enquiry"
          )}
        </CTAButton>
      </div>
    </form>
  );
};

export default ContactForm;
